import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import {
  Rocket,
  Megaphone,
  Upload,
  BarChart3,
  Sparkles,
  Send,
  Globe,
  FileCode,
  Bell,
} from "lucide-react";
import { useUIMode } from "@/lib/ui-mode";
import { HelpPanel } from "@/components/app/help-panel";

export const Route = createFileRoute("/_authenticated/start")({
  component: StartPage,
  head: () => ({ meta: [{ title: "Start — InboxPulse" }] }),
});

const PRIMARY_TILES = [
  {
    to: "/campaign-wizard",
    icon: Rocket,
    title: "Kampagne starten",
    desc: "Schritt-für-Schritt: Name → Tracking → Empfänger → Betreff → HTML → Versand.",
    cta: "Wizard öffnen",
  },
  {
    to: "/recipient-import",
    icon: Upload,
    title: "Empfänger importieren",
    desc: "CSV mit E-Mail-Adressen hochladen und für Kampagnen verfügbar machen.",
    cta: "CSV hochladen",
  },
  {
    to: "/dashboard",
    icon: BarChart3,
    title: "Statistiken ansehen",
    desc: "Wer hat geöffnet, geklickt oder ist gebounct? Live-Übersicht.",
    cta: "Dashboard öffnen",
  },
  {
    to: "/assistant",
    icon: Sparkles,
    title: "KI fragen",
    desc: "Du weißt nicht weiter? Der eingebaute Assistent erklärt jeden Schritt.",
    cta: "Chat öffnen",
  },
];

const SECONDARY_TILES = [
  { to: "/campaigns", icon: Megaphone, title: "Meine Kampagnen" },
  { to: "/smtp-send", icon: Send, title: "SMTP-Versand" },
  { to: "/domains", icon: Globe, title: "Domains" },
  { to: "/analyzer", icon: FileCode, title: "HTML-Prüfer" },
  { to: "/alerts", icon: Bell, title: "Alarme" },
];

function StartPage() {
  const { mode } = useUIMode();

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Was möchtest du tun?</h1>
        <p className="text-muted-foreground mt-1">
          Wähle einen der häufigsten Schritte – oder frag die KI, wenn du nicht weiter weißt.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 mb-8">
        {PRIMARY_TILES.map((t) => (
          <Link key={t.to} to={t.to} className="group">
            <Card className="p-6 h-full bg-card/60 border-border hover:border-primary/50 hover:bg-card transition">
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-xl bg-[image:var(--gradient-primary)] grid place-items-center shadow-[var(--shadow-glow)] shrink-0">
                  <t.icon className="size-6 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">{t.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{t.desc}</p>
                  <span className="inline-block mt-3 text-sm text-primary group-hover:underline">
                    {t.cta} →
                  </span>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mb-8">
        <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-3">
          Schnellzugriff
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {SECONDARY_TILES.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className="p-4 rounded-lg border border-border bg-card/40 hover:bg-card hover:border-primary/40 transition text-center"
            >
              <t.icon className="size-5 mx-auto mb-2 text-muted-foreground" />
              <span className="text-sm">{t.title}</span>
            </Link>
          ))}
        </div>
      </div>

      <HelpPanel
        title="Tipp: Anfänger- oder Profi-Modus?"
        what={
          <>
            Oben rechts kannst du zwischen <b>Anfänger</b> und <b>Profi</b> umschalten. Im
            Anfänger-Modus ({mode === "beginner" ? "aktuell aktiv" : "nicht aktiv"}) blenden wir
            technische Seiten wie Queue-Health, Pixel-Diagnose oder Dead-Letter aus, damit du dich
            nicht verlierst.
          </>
        }
        why="Profis sehen alle 40+ Seiten. Anfänger sehen nur die ca. 15 wirklich relevanten."
        tips={[
          <>
            Wenn du irgendwo nicht weiter weißt, klick rechts unten auf den lila Stern – das ist die
            KI.
          </>,
          <>Die KI weiß, auf welcher Seite du bist, und kann dir gezielt helfen.</>,
        ]}
      />
    </div>
  );
}
